/**
 * talentMobility.mock.js
 *
 * There is no live Talent Mobility backend yet (see plan doc). This module
 * is the single source of mock data for every Talent Mobility screen, shaped
 * like a plausible future API response so real endpoints can be swapped in
 * behind useTalentMobilityData.js without changing any page component.
 *
 * Proficiency values are 0-5, matching SkillsBridger's own scale
 * (see talentMobility.constants.js).
 */

// ─── Skills catalog ───────────────────────────────────────────────────────────

// `description` mirrors SkillsBridger's own skill-name hover pattern
// (Pages/SkillsBridgerV2/HrSkillsBridger/GroupSkillsProficiency/components/SkillNameCell)
// — a short rationale shown truncated under the skill name and in full on
// hover. No live inference engine here, so these are hand-authored blurbs
// rather than model-generated notes.
export const SKILLS_CATALOG = [
  { id: "sk-sql", name: "SQL & Query Design", description: "Writes and optimises complex queries across relational data models — joins, window functions, and query plans, not just basic SELECTs." },
  { id: "sk-python", name: "Python / Data Engineering", description: "Builds and maintains data pipelines and transformation scripts in Python, including working with common data engineering libraries." },
  { id: "sk-stats", name: "Statistical Analysis", description: "Applies statistical methods (hypothesis testing, regression, distributions) to draw defensible conclusions from data rather than just summarising it." },
  { id: "sk-viz", name: "Data Visualisation", description: "Turns analysis into charts and dashboards that a non-technical stakeholder can act on, choosing the right visual for the question being answered." },
  { id: "sk-stakeholder", name: "Stakeholder Communication", description: "Translates technical findings into business language, manages expectations, and drives alignment across functions without a formal reporting line." },
  { id: "sk-people", name: "People Management", description: "Owns hiring, performance, and career development for direct reports — coaching individuals rather than only managing their output." },
  { id: "sk-arch", name: "Systems Architecture", description: "Designs system boundaries, data flows, and scaling strategy for a service or platform, weighing trade-offs before implementation begins." },
  { id: "sk-cloud", name: "Cloud Infrastructure", description: "Provisions, secures, and operates cloud infrastructure (compute, storage, networking) with an eye on cost and reliability, not just getting it running." },
  { id: "sk-code-review", name: "Code Review & Mentoring", description: "Reviews others' code for correctness, maintainability, and adherence to standards, and uses review as a coaching moment for less senior engineers." },
  { id: "sk-project", name: "Project Management", description: "Plans scope, sequencing, and dependencies for multi-person initiatives, and keeps stakeholders informed when timelines shift." },
  { id: "sk-strategy", name: "Org Strategy", description: "Sets multi-quarter direction for a function — where to invest, what to deprioritise — informed by both market context and internal capability." },
];

// ─── Roles (org hierarchy via reportsToRoleId) ───────────────────────────────
// `team` powers the mock HR-Manager-vs-Line-Manager scope toggle (req #5).
// `priority` is read-only here — sourced from Jobs Library in the real build.

// `legalEntity` and `vacancyCount` back the Redeployment Center's standard
// filter row and the Roles List "Positions" column respectively (req #9/#10).
// `involvesGeneralManagement` is a manual per-role tag (like `priority`/
// `level` — sourced from Jobs Library in the real build, not derived) that
// powers the Employee Career Pathways "Involves General Management" filter —
// true for roles that are primarily about managing people/budget rather than
// individual-contributor craft.
// `currentPositionCount`/`targetPositionCount` back the Redeployment Center's
// two new editable columns — manual placeholders for now (no HRIS import
// yet); `currentPositionCount` mirrors how many PEOPLE records currently
// hold this role, `targetPositionCount` = current + vacancyCount.
export const ROLES = [
  {
    id: "r-cto",
    title: "Chief Technology Officer",
    businessUnit: "Enterprise",
    function: "Technology",
    subFunction: "Executive",
    location: "Remote",
    legalEntity: "SkillsBridge Inc",
    level: "L9",
    team: "Executive",
    reportsToRoleId: null,
    occupantId: "p-cto",
    priority: "High",
    status: "Closed",
    discoverable: false,
    vacancyCount: 0,
    involvesGeneralManagement: true,
    currentPositionCount: 1,
    targetPositionCount: 1,
  },
  {
    id: "r-vp-eng",
    title: "VP, Engineering",
    businessUnit: "Enterprise",
    function: "Engineering",
    subFunction: "Leadership",
    location: "Bangalore",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L8",
    team: "Engineering — Platform",
    reportsToRoleId: "r-cto",
    occupantId: "p-vp-eng",
    priority: "High",
    status: "Closed",
    discoverable: false,
    vacancyCount: 0,
    involvesGeneralManagement: true,
    currentPositionCount: 1,
    targetPositionCount: 1,
  },
  {
    id: "r-vp-data",
    title: "VP, Data & Analytics",
    businessUnit: "Enterprise",
    function: "Data & Analytics",
    subFunction: "Leadership",
    location: "Mumbai",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L8",
    team: "Data & Analytics",
    reportsToRoleId: "r-cto",
    occupantId: "p-vp-data",
    priority: "High",
    status: "Closed",
    discoverable: false,
    vacancyCount: 0,
    involvesGeneralManagement: true,
    currentPositionCount: 1,
    targetPositionCount: 1,
  },
  {
    id: "r-em-platform",
    title: "Engineering Manager — Platform",
    businessUnit: "Platform",
    function: "Engineering",
    subFunction: "Platform Engineering",
    location: "Bangalore",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L6",
    team: "Engineering — Platform",
    reportsToRoleId: "r-vp-eng",
    occupantId: "p-em-platform",
    priority: "High",
    status: "Open",
    discoverable: true,
    vacancyCount: 1,
    involvesGeneralManagement: true,
    currentPositionCount: 1,
    targetPositionCount: 2,
  },
  {
    id: "r-em-data-eng",
    title: "Engineering Manager — Data",
    businessUnit: "Platform",
    function: "Engineering",
    subFunction: "Data Engineering",
    location: "Hyderabad",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L6",
    team: "Engineering — Data",
    reportsToRoleId: "r-vp-eng",
    occupantId: "p-em-data",
    priority: "Normal",
    status: "Closed",
    discoverable: false,
    vacancyCount: 0,
    involvesGeneralManagement: true,
    currentPositionCount: 1,
    targetPositionCount: 1,
  },
  {
    id: "r-analytics-mgr",
    title: "Analytics Manager",
    businessUnit: "Enterprise",
    function: "Data & Analytics",
    subFunction: "Analytics",
    location: "Mumbai",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L6",
    team: "Data & Analytics",
    reportsToRoleId: "r-vp-data",
    occupantId: "p-analytics-mgr",
    priority: "High",
    status: "Open",
    discoverable: true,
    vacancyCount: 1,
    involvesGeneralManagement: true,
    currentPositionCount: 1,
    targetPositionCount: 2,
  },
  {
    id: "r-sr-swe",
    title: "Sr. Software Engineer",
    businessUnit: "Platform",
    function: "Engineering",
    subFunction: "Platform Engineering",
    location: "Bangalore",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L4",
    team: "Engineering — Platform",
    reportsToRoleId: "r-em-platform",
    occupantId: "p-arjun",
    priority: "Normal",
    status: "Open",
    discoverable: true,
    vacancyCount: 2,
    involvesGeneralManagement: false,
    currentPositionCount: 1,
    targetPositionCount: 3,
  },
  {
    id: "r-swe-ii",
    title: "Software Engineer II",
    businessUnit: "Platform",
    function: "Engineering",
    subFunction: "Platform Engineering",
    location: "Remote",
    legalEntity: "SkillsBridge Inc",
    level: "L3",
    team: "Engineering — Platform",
    reportsToRoleId: "r-em-platform",
    occupantId: "p-wei",
    priority: "Low",
    status: "Open",
    discoverable: false,
    vacancyCount: 1,
    involvesGeneralManagement: false,
    currentPositionCount: 1,
    targetPositionCount: 2,
  },
  {
    id: "r-sr-data-eng",
    title: "Sr. Data Engineer",
    businessUnit: "Platform",
    function: "Engineering",
    subFunction: "Data Engineering",
    location: "Hyderabad",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L4",
    team: "Engineering — Data",
    reportsToRoleId: "r-em-data-eng",
    occupantId: "p-kavya",
    priority: "Normal",
    status: "Closed",
    discoverable: false,
    vacancyCount: 0,
    involvesGeneralManagement: false,
    currentPositionCount: 1,
    targetPositionCount: 1,
  },
  {
    id: "r-sr-analyst",
    title: "Sr. Data Analyst",
    businessUnit: "Enterprise",
    function: "Data & Analytics",
    subFunction: "Analytics",
    location: "Mumbai",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L4",
    team: "Data & Analytics",
    reportsToRoleId: "r-analytics-mgr",
    occupantId: "p-priya",
    priority: "High",
    status: "Open",
    discoverable: true,
    vacancyCount: 2,
    involvesGeneralManagement: false,
    currentPositionCount: 1,
    targetPositionCount: 3,
  },
  {
    id: "r-business-analyst",
    title: "Business Analyst",
    businessUnit: "Enterprise",
    function: "Data & Analytics",
    subFunction: "Analytics",
    location: "Pune",
    legalEntity: "SkillsBridge India Pvt Ltd",
    level: "L3",
    team: "Data & Analytics",
    reportsToRoleId: "r-analytics-mgr",
    occupantId: "p-divya",
    priority: "Normal",
    status: "Open",
    discoverable: true,
    vacancyCount: 3,
    involvesGeneralManagement: false,
    currentPositionCount: 2,
    targetPositionCount: 5,
  },
];

// ─── People ────────────────────────────────────────────────────────────────
// `joiningDate`/`augmentationPotential`/`previousRoles`/`contact`/`badges`/
// `completedCourses`/`ongoingCourses` back the Candidate Profile detail view
// (Figma: profile card on left, Skills/Completed Courses/Ongoing Courses
// tabs on right) — reached via "View Detailed Profile" from a skill-gap
// accordion.

// `aiSummary` mirrors SkillsBridger's own "Individual Profile Summary" panel
// (Pages/SkillsBridgerV2/HrSkillsBridger/SkillsMap/components/EmployeeProfileCard)
// — an 8-section LLM-generated summary shown once an employee has uploaded
// their profile. No live LLM call in Talent Mobility yet, so these are
// hand-authored stand-ins matching the exact same section shape/keys as the
// real `ai_generated_summary.employee_summary` response, so a real API can
// slot in later without changing the component that renders them.
export const PEOPLE = [
  {
    id: "p-cto", employeeId: "SB-10001", name: "Meera Iyer", currentRoleId: "r-cto", team: "Executive",
    joiningDate: "2019-03-11", augmentationPotential: 35,
    previousRoles: [{ title: "VP, Engineering", dateRange: "Jan 2016 – Mar 2019" }],
    contact: { location: "Remote", phone: "+1 415 555 0142", email: "meera.iyer@skillsbridge.ai" },
    badges: ["🏆", "🎖️"],
    completedCourses: [{ id: "c-1", name: "Executive Leadership Foundations", completedDate: "2025-02-10" }],
    ongoingCourses: [{ id: "c-2", name: "Scaling Engineering Orgs", progressPct: 60 }],
    aiSummary: {
      skills_competencies: "Deep technical breadth across systems architecture and cloud infrastructure, paired with strong org-design judgment built over 6+ years of engineering leadership.",
      career_aspirations: "Has expressed interest in expanding scope toward a Chief Product & Technology role that spans both engineering and product strategy.",
      learning_development_needs: "Would benefit from structured exposure to board-level financial reporting, an area outside her current experience.",
      engagement_motivation: "Highly engaged; motivated by ambitious technical bets and mentoring senior engineering leaders.",
      performance_potential: "Consistently exceeds expectations; top-decile performer across the last 3 review cycles.",
      internal_mobility: "Open to relocation for the right scope; has not indicated interest in moving roles in the near term.",
      managerial_behavioral_skills: "Strong at delegating ownership and running structured decision-making forums; occasionally over-indexes on technical detail in exec settings.",
      retention_risk: "Low — strong tenure trajectory and no flight-risk signals detected.",
    },
  },
  {
    id: "p-vp-eng", employeeId: "SB-10002", name: "Sanjay Rao", currentRoleId: "r-vp-eng", team: "Engineering — Platform",
    joiningDate: "2020-06-01", augmentationPotential: 40,
    previousRoles: [{ title: "Engineering Manager — Platform", dateRange: "Jun 2018 – May 2020" }],
    contact: { location: "Bangalore", phone: "+91 98450 11223", email: "sanjay.rao@skillsbridge.ai" },
    badges: ["🥇"],
    completedCourses: [{ id: "c-3", name: "Cloud Architecture Deep Dive", completedDate: "2024-11-02" }],
    ongoingCourses: [],
    aiSummary: {
      skills_competencies: "Strong platform engineering fundamentals with proven ability to scale infrastructure teams through hypergrowth.",
      career_aspirations: "Interested in a CTO-track role within 3-4 years; has discussed this openly with his manager.",
      learning_development_needs: "Would benefit from broader cross-functional exposure outside engineering (e.g. GTM, finance).",
      engagement_motivation: "Energized by solving hard infrastructure scaling problems; less engaged by process-heavy initiatives.",
      performance_potential: "Strong, steady performer; well regarded by peers and reports.",
      internal_mobility: "Open to lateral moves that broaden scope even without a title change.",
      managerial_behavioral_skills: "Direct, clear communicator; builds trust quickly with senior stakeholders.",
      retention_risk: "Low.",
    },
  },
  {
    id: "p-vp-data", employeeId: "SB-10003", name: "Neha Kapoor", currentRoleId: "r-vp-data", team: "Data & Analytics",
    joiningDate: "2019-09-16", augmentationPotential: 38,
    previousRoles: [{ title: "Analytics Manager", dateRange: "Sep 2016 – Aug 2019" }],
    contact: { location: "Mumbai", phone: "+91 98200 33445", email: "neha.kapoor@skillsbridge.ai" },
    badges: ["🏆", "⭐"],
    completedCourses: [{ id: "c-4", name: "Data Strategy for Leaders", completedDate: "2025-01-20" }],
    ongoingCourses: [{ id: "c-5", name: "Org Strategy Masterclass", progressPct: 25 }],
    aiSummary: {
      skills_competencies: "Sets data strategy at the org level; strong at translating analytics into business outcomes for the exec team.",
      career_aspirations: "Long-tenured in data leadership; has signaled interest in a General Manager-style P&L role longer-term.",
      learning_development_needs: "P&L ownership experience would round out her leadership profile for a GM-track move.",
      engagement_motivation: "Motivated by strategic influence and mentoring the analytics function's next generation of managers.",
      performance_potential: "Top performer; consistently drives measurable org-wide impact.",
      internal_mobility: "Not actively seeking a move, but open to the right stretch opportunity.",
      managerial_behavioral_skills: "Collaborative, calm under pressure, strong at aligning cross-functional stakeholders.",
      retention_risk: "Low.",
    },
  },
  {
    id: "p-em-platform", employeeId: "SB-10004", name: "Rahul Gupta", currentRoleId: "r-em-platform", team: "Engineering — Platform",
    joiningDate: "2021-04-01", augmentationPotential: 55,
    previousRoles: [{ title: "Sr. Software Engineer", dateRange: "Apr 2019 – Mar 2021" }],
    contact: { location: "Bangalore", phone: "+91 98765 44120", email: "rahul.gupta@skillsbridge.ai" },
    badges: ["🥇", "🚀"],
    completedCourses: [{ id: "c-6", name: "Systems Architecture Fundamentals", completedDate: "2024-08-14" }],
    ongoingCourses: [{ id: "c-7", name: "People Management Essentials", progressPct: 45 }],
    aiSummary: {
      skills_competencies: "Recently promoted into people management from a strong IC track; still building management muscle alongside solid technical depth.",
      career_aspirations: "Wants to grow into a Director of Engineering role within 2-3 years.",
      learning_development_needs: "Actively developing people-management fundamentals — currently mid-way through a management essentials course.",
      engagement_motivation: "Motivated by team growth and unblocking others; enjoys coaching more than he expected to.",
      performance_potential: "High-potential; early management performance is promising.",
      internal_mobility: "Not currently seeking a move — focused on establishing himself in the manager role.",
      managerial_behavioral_skills: "Still developing — strong technical credibility with the team, working on delegation and giving difficult feedback.",
      retention_risk: "Low.",
    },
  },
  {
    id: "p-em-data", employeeId: "SB-10005", name: "Sana Sheikh", currentRoleId: "r-em-data-eng", team: "Engineering — Data",
    joiningDate: "2021-07-19", augmentationPotential: 50,
    previousRoles: [{ title: "Sr. Data Engineer", dateRange: "Jul 2019 – Jun 2021" }],
    contact: { location: "Hyderabad", phone: "+91 90000 12345", email: "sana.sheikh@skillsbridge.ai" },
    badges: ["🎖️"],
    completedCourses: [],
    ongoingCourses: [{ id: "c-8", name: "Cloud Infrastructure at Scale", progressPct: 30 }],
    aiSummary: {
      skills_competencies: "Solid data engineering management background with hands-on infrastructure depth.",
      career_aspirations: "Comfortable in current scope; hasn't signaled strong near-term aspiration for a larger role.",
      learning_development_needs: "Broader cloud infrastructure exposure at scale would strengthen her platform credibility.",
      engagement_motivation: "Steady, consistent engagement; motivated by team stability and delivery predictability.",
      performance_potential: "Solid, dependable performer.",
      internal_mobility: "No stated mobility preference at this time.",
      managerial_behavioral_skills: "Even-keeled, fair, well-liked by her team.",
      retention_risk: "Low-to-moderate — has been in the same scope for a while without a recent stretch assignment.",
    },
  },
  {
    id: "p-analytics-mgr", employeeId: "SB-10006", name: "Vikram Das", currentRoleId: "r-analytics-mgr", team: "Data & Analytics",
    joiningDate: "2020-02-03", augmentationPotential: 48,
    previousRoles: [{ title: "Sr. Data Analyst", dateRange: "Feb 2018 – Jan 2020" }],
    contact: { location: "Mumbai", phone: "+91 98220 55667", email: "vikram.das@skillsbridge.ai" },
    badges: ["🏆"],
    completedCourses: [{ id: "c-9", name: "Stakeholder Communication", completedDate: "2024-06-05" }],
    ongoingCourses: [],
    aiSummary: {
      skills_competencies: "Strong analytics management fundamentals with excellent stakeholder communication skills.",
      career_aspirations: "Interested in a VP, Data & Analytics track as the org scales.",
      learning_development_needs: "Would benefit from more exposure to org-wide strategy setting, not just team-level execution.",
      engagement_motivation: "Highly engaged; enjoys translating ambiguous asks into structured analytics roadmaps.",
      performance_potential: "Strong performer with clear growth trajectory.",
      internal_mobility: "Open to broader scope within Data & Analytics.",
      managerial_behavioral_skills: "Clear communicator, strong at managing up and across; developing skill at managing larger teams.",
      retention_risk: "Low.",
    },
  },
  {
    id: "p-arjun", employeeId: "SB-10007", name: "Arjun Kumar", currentRoleId: "r-sr-swe", team: "Engineering — Platform",
    joiningDate: "2022-01-10", augmentationPotential: 62,
    previousRoles: [
      { title: "Software Engineer II", dateRange: "Jan 2020 – Dec 2021" },
      { title: "Software Engineer I", dateRange: "Jun 2018 – Dec 2019" },
    ],
    contact: { location: "Bangalore", phone: "+91 90090 88771", email: "arjun.kumar@skillsbridge.ai" },
    badges: ["🚀", "⭐"],
    completedCourses: [{ id: "c-10", name: "Code Review Best Practices", completedDate: "2024-09-22" }],
    ongoingCourses: [{ id: "c-11", name: "Systems Architecture for Engineers", progressPct: 70 }],
    aiSummary: {
      skills_competencies: "Strong individual-contributor engineer with growing systems-architecture depth; increasingly trusted with design ownership.",
      career_aspirations: "Interested in either a Staff Engineer track or a first management role — still deciding which path fits best.",
      learning_development_needs: "Systems architecture coursework in progress will round out his readiness for either track.",
      engagement_motivation: "Motivated by technically ambitious projects; enjoys mentoring junior engineers informally already.",
      performance_potential: "High-potential; frequently cited as a strong influence on team code quality.",
      internal_mobility: "Open to a lateral move that clarifies his IC-vs-management path.",
      managerial_behavioral_skills: "Not yet in a formal management role, but shows early coaching instincts with junior engineers.",
      retention_risk: "Moderate — ambitious and could look externally if internal growth path isn't clarified soon.",
    },
  },
  {
    id: "p-wei", employeeId: "SB-10008", name: "Wei Zhang", currentRoleId: "r-swe-ii", team: "Engineering — Platform",
    joiningDate: "2023-03-06", augmentationPotential: 58,
    previousRoles: [{ title: "Software Engineer I", dateRange: "Mar 2021 – Feb 2023" }],
    contact: { location: "Remote", phone: "+1 628 555 0199", email: "wei.zhang@skillsbridge.ai" },
    badges: ["⭐"],
    completedCourses: [],
    ongoingCourses: [
      { id: "c-12", name: "Cloud Infrastructure Fundamentals", progressPct: 40 },
      { id: "c-13", name: "SQL for Engineers", progressPct: 80 },
    ],
    aiSummary: {
      skills_competencies: "Solid mid-level engineering fundamentals, actively broadening into cloud infrastructure and data querying.",
      career_aspirations: "Early career; still exploring which engineering specialization to lean into.",
      learning_development_needs: "Two courses in progress (cloud infra, SQL) directly address current skill gaps for a Sr. Software Engineer move.",
      engagement_motivation: "Curious and coachable; responds well to stretch assignments.",
      performance_potential: "Promising — trending upward each review cycle.",
      internal_mobility: "Open to relocation; has expressed interest in data-engineering-adjacent roles.",
      managerial_behavioral_skills: "Not yet applicable — no direct reports.",
      retention_risk: "Low-to-moderate — early in tenure, engagement will hinge on visible growth path.",
    },
  },
  {
    id: "p-kavya", employeeId: "SB-10009", name: "Kavya Nair", currentRoleId: "r-sr-data-eng", team: "Engineering — Data",
    joiningDate: "2021-11-15", augmentationPotential: 52,
    previousRoles: [{ title: "Data Engineer", dateRange: "Nov 2019 – Oct 2021" }],
    contact: { location: "Hyderabad", phone: "+91 90140 66778", email: "kavya.nair@skillsbridge.ai" },
    badges: ["🥇", "🎖️"],
    completedCourses: [{ id: "c-14", name: "Python for Data Engineering", completedDate: "2024-12-01" }],
    ongoingCourses: [],
    aiSummary: {
      skills_competencies: "Strong senior data engineering skillset with solid Python and cloud fundamentals.",
      career_aspirations: "Interested in an Engineering Manager — Data role as a next step.",
      learning_development_needs: "People-management exposure would be the main gap for a manager-track move.",
      engagement_motivation: "Motivated by clean, reliable pipeline design; enjoys ownership of end-to-end systems.",
      performance_potential: "Strong, reliable performer.",
      internal_mobility: "Open to a management-track move within Data Engineering.",
      managerial_behavioral_skills: "No formal reports yet, but informally mentors newer data engineers already.",
      retention_risk: "Low.",
    },
  },
  {
    id: "p-priya", employeeId: "SB-10010", name: "Priya Sharma", currentRoleId: "r-sr-analyst", team: "Data & Analytics",
    joiningDate: "2020-08-24", augmentationPotential: 45,
    previousRoles: [{ title: "Data Analyst", dateRange: "Aug 2018 – Jul 2020" }],
    contact: { location: "Mumbai", phone: "+91 98330 99887", email: "priya.sharma@skillsbridge.ai" },
    badges: ["🏆", "🥇", "⭐"],
    completedCourses: [{ id: "c-15", name: "Statistical Analysis Advanced", completedDate: "2025-03-02" }],
    ongoingCourses: [{ id: "c-16", name: "People Management for Analysts", progressPct: 15 }],
    aiSummary: {
      skills_competencies: "Excellent statistical analysis and stakeholder communication skills; one of the strongest senior analysts on the team.",
      career_aspirations: "Actively pursuing an Analytics Manager path — enrolled in a people-management course to prepare.",
      learning_development_needs: "People management is the clear, actively-being-addressed gap for her next move.",
      engagement_motivation: "Highly engaged; motivated by growing into a leadership role.",
      performance_potential: "Top performer — one of the highest-rated analysts in the org.",
      internal_mobility: "Actively seeking the next role up within Data & Analytics.",
      managerial_behavioral_skills: "Early-stage — coursework in progress; shows natural mentoring instincts with junior analysts.",
      retention_risk: "Moderate — strong performer who will look elsewhere if a management path doesn't open up soon.",
    },
  },
  {
    id: "p-divya", employeeId: "SB-10011", name: "Divya Krishnan", currentRoleId: "r-business-analyst", team: "Data & Analytics",
    joiningDate: "2022-05-30", augmentationPotential: 60,
    previousRoles: [{ title: "Junior Business Analyst", dateRange: "May 2021 – Apr 2022" }],
    contact: { location: "Pune", phone: "+91 98600 11234", email: "divya.krishnan@skillsbridge.ai" },
    badges: ["🚀"],
    completedCourses: [],
    ongoingCourses: [{ id: "c-17", name: "Data Visualisation with SQL", progressPct: 55 }],
    aiSummary: {
      skills_competencies: "Solid business analyst fundamentals, growing data visualization skillset.",
      career_aspirations: "Interested in a Sr. Data Analyst move within the next year.",
      learning_development_needs: "SQL-based visualization course in progress directly targets the Sr. Data Analyst skill gap.",
      engagement_motivation: "Engaged and proactive about closing her own skill gaps.",
      performance_potential: "Promising, steady improvement each cycle.",
      internal_mobility: "Open to the next role up in the Analytics track.",
      managerial_behavioral_skills: "Not yet applicable — no direct reports.",
      retention_risk: "Low.",
    },
  },
  {
    id: "p-rohan", employeeId: "SB-10012", name: "Rohan Kapoor", currentRoleId: "r-business-analyst", team: "Data & Analytics",
    joiningDate: "2022-09-12", augmentationPotential: 50,
    previousRoles: [{ title: "Business Analyst Intern", dateRange: "Jan 2022 – Aug 2022" }],
    contact: { location: "Pune", phone: "+91 98600 55432", email: "rohan.kapoor@skillsbridge.ai" },
    badges: ["⭐", "🎖️"],
    completedCourses: [{ id: "c-18", name: "Stakeholder Communication Basics", completedDate: "2024-10-18" }],
    ongoingCourses: [{ id: "c-19", name: "Statistical Analysis Foundations", progressPct: 35 }],
    aiSummary: {
      skills_competencies: "Solid foundational business analyst skills with strengthening stakeholder communication.",
      career_aspirations: "Has stated interest in a broader leadership path (this is Talent Mobility's own mock \"current employee\" — see CURRENT_EMPLOYEE_ID).",
      learning_development_needs: "Statistical analysis foundations course in progress; would need broader technical/leadership depth for a data-leadership track.",
      engagement_motivation: "Engaged, curious about paths beyond a single-function track.",
      performance_potential: "Promising early-career trajectory.",
      internal_mobility: "Actively exploring internal mobility options — see his stated target role in Aspiration Capture.",
      managerial_behavioral_skills: "Not yet applicable — no direct reports.",
      retention_risk: "Low-to-moderate — engagement will hinge on a visible path toward his stated aspiration.",
    },
  },
];

// The mock "currently logged in employee" for /talent-mobility-employee.
export const CURRENT_EMPLOYEE_ID = "p-rohan";

// Time-in-role and current SRI-on-own-role — drives Succession Planning's
// per-box stats and "Ready for Next Role" flag.
export const ROLE_OCCUPANCY = {
  "r-cto":            { timeInRoleMonths: 41, sri: 96 },
  "r-vp-eng":         { timeInRoleMonths: 33, sri: 93 },
  "r-vp-data":        { timeInRoleMonths: 27, sri: 91 },
  "r-em-platform":    { timeInRoleMonths: 19, sri: 88 },
  "r-em-data-eng":    { timeInRoleMonths: 14, sri: 79 },
  "r-analytics-mgr":  { timeInRoleMonths: 22, sri: 94 },
  "r-sr-swe":         { timeInRoleMonths: 18, sri: 84 },
  "r-swe-ii":         { timeInRoleMonths: 9,  sri: 66 },
  "r-sr-data-eng":    { timeInRoleMonths: 11, sri: 77 },
  "r-sr-analyst":     { timeInRoleMonths: 16, sri: 91 },
  "r-business-analyst": { timeInRoleMonths: 7,  sri: 61 },
};

// ─── Role skill requirements ──────────────────────────────────────────────────

export const ROLE_SKILLS = {
  "r-em-platform": [
    { skillId: "sk-arch", required: 4 },
    { skillId: "sk-cloud", required: 4 },
    { skillId: "sk-people", required: 4 },
    { skillId: "sk-code-review", required: 3 },
  ],
  "r-sr-swe": [
    { skillId: "sk-arch", required: 3 },
    { skillId: "sk-cloud", required: 3 },
    { skillId: "sk-code-review", required: 3 },
    { skillId: "sk-project", required: 2 },
  ],
  "r-analytics-mgr": [
    { skillId: "sk-stats", required: 4 },
    { skillId: "sk-stakeholder", required: 4 },
    { skillId: "sk-people", required: 3 },
    { skillId: "sk-strategy", required: 3 },
  ],
  "r-sr-analyst": [
    { skillId: "sk-sql", required: 4 },
    { skillId: "sk-python", required: 3 },
    { skillId: "sk-stats", required: 4 },
    { skillId: "sk-stakeholder", required: 3 },
  ],
  "r-sr-data-eng": [
    { skillId: "sk-python", required: 4 },
    { skillId: "sk-cloud", required: 4 },
    { skillId: "sk-arch", required: 3 },
  ],
  "r-business-analyst": [
    { skillId: "sk-sql", required: 3 },
    { skillId: "sk-viz", required: 3 },
    { skillId: "sk-stakeholder", required: 3 },
  ],
};

// ─── Person skill proficiencies (current) ────────────────────────────────────

export const PERSON_SKILLS = {
  "p-arjun": [
    { skillId: "sk-arch", current: 3 },
    { skillId: "sk-cloud", current: 3 },
    { skillId: "sk-code-review", current: 3 },
    { skillId: "sk-project", current: 2 },
    { skillId: "sk-people", current: 2 },
    { skillId: "sk-stakeholder", current: 3 },
  ],
  "p-wei": [
    { skillId: "sk-arch", current: 2 },
    { skillId: "sk-cloud", current: 2 },
    { skillId: "sk-code-review", current: 2 },
    { skillId: "sk-sql", current: 3 },
    { skillId: "sk-stats", current: 2 },
  ],
  "p-priya": [
    { skillId: "sk-sql", current: 4 },
    { skillId: "sk-python", current: 3 },
    { skillId: "sk-stats", current: 4 },
    { skillId: "sk-stakeholder", current: 3 },
    { skillId: "sk-people", current: 2 },
    { skillId: "sk-viz", current: 4 },
  ],
  "p-divya": [
    { skillId: "sk-sql", current: 3 },
    { skillId: "sk-viz", current: 3 },
    { skillId: "sk-stakeholder", current: 2 },
    { skillId: "sk-stats", current: 2 },
  ],
  "p-rohan": [
    { skillId: "sk-sql", current: 3 },
    { skillId: "sk-viz", current: 4 },
    { skillId: "sk-stakeholder", current: 3 },
    { skillId: "sk-python", current: 2 },
    { skillId: "sk-stats", current: 3 },
    { skillId: "sk-people", current: 2 },
  ],
  "p-kavya": [
    { skillId: "sk-python", current: 4 },
    { skillId: "sk-cloud", current: 3 },
    { skillId: "sk-arch", current: 3 },
  ],
  "p-em-platform": [
    { skillId: "sk-arch", current: 4 },
    { skillId: "sk-cloud", current: 4 },
    { skillId: "sk-people", current: 4 },
    { skillId: "sk-code-review", current: 4 },
  ],
  "p-analytics-mgr": [
    { skillId: "sk-stats", current: 4 },
    { skillId: "sk-stakeholder", current: 4 },
    { skillId: "sk-people", current: 3 },
    { skillId: "sk-strategy", current: 3 },
  ],
};

// ─── Match pairs (role x person SRI) ─────────────────────────────────────────
// Powers Role Detail's successor list (role → people) AND Redeployment
// Center (person → roles). Both read this one array — see req #13: this is
// intentionally person-led everywhere, not a bidirectional toggle.

export const MATCH_PAIRS = [
  { roleId: "r-em-platform", personId: "p-arjun", sri: 84 },
  { roleId: "r-em-platform", personId: "p-wei", sri: 58 },
  { roleId: "r-em-platform", personId: "p-kavya", sri: 66 },
  { roleId: "r-analytics-mgr", personId: "p-priya", sri: 91 },
  { roleId: "r-analytics-mgr", personId: "p-divya", sri: 63 },
  { roleId: "r-analytics-mgr", personId: "p-rohan", sri: 60 },
  { roleId: "r-sr-swe", personId: "p-wei", sri: 71 },
  { roleId: "r-sr-analyst", personId: "p-divya", sri: 74 },
  { roleId: "r-sr-analyst", personId: "p-rohan", sri: 69 },
  { roleId: "r-business-analyst", personId: "p-rohan", sri: 88 },
  { roleId: "r-sr-data-eng", personId: "p-wei", sri: 52 },
];

export const getPeopleForRole = (roleId) =>
  MATCH_PAIRS.filter((m) => m.roleId === roleId)
    .map((m) => ({ ...m, person: PEOPLE.find((p) => p.id === m.personId) }))
    .sort((a, b) => b.sri - a.sri);

export const getRolesForPerson = (personId) =>
  MATCH_PAIRS.filter((m) => m.personId === personId)
    .map((m) => ({ ...m, role: ROLES.find((r) => r.id === m.roleId) }))
    .filter((m) => m.role && m.role.occupantId !== personId)
    .sort((a, b) => b.sri - a.sri);

export const getSkillGap = (roleId, personId) => {
  const required = ROLE_SKILLS[roleId] || [];
  const current = PERSON_SKILLS[personId] || [];
  const currentBySkill = Object.fromEntries(current.map((s) => [s.skillId, s.current]));
  return required.map((req) => {
    const skill = SKILLS_CATALOG.find((s) => s.id === req.skillId);
    return {
      skillId: req.skillId,
      skillName: skill?.name || req.skillId,
      description: skill?.description || null,
      required: req.required,
      current: currentBySkill[req.skillId] ?? 0,
      priority: req.required >= 4,
    };
  });
};

// Priority skills a candidate hasn't yet met the bar on — powers the
// "Critical Skills Gaps to Bridge" table column (Potential Internal
// Candidates screen).
export const getCriticalSkillGaps = (roleId, personId) =>
  getSkillGap(roleId, personId).filter((s) => s.priority && s.current < s.required);

// Candidate Profile detail view (Figma: profile card + Skills/Completed
// Courses/Ongoing Courses tabs) — combines the person's own record with
// their current role title.
export const getCandidateProfile = (personId) => {
  const person = PEOPLE.find((p) => p.id === personId);
  if (!person) return null;
  const currentRole = ROLES.find((r) => r.id === person.currentRoleId);
  return { ...person, currentRoleTitle: currentRole?.title || "—" };
};

// Shared by pathway opportunities AND the org-wide "other high matches" scan
// (see useOpenPositionsForCurrentEmployee) — one calculation, used both places
// so an off-pathway suggestion and its on-pathway counterpart never disagree.
export const computeMatchForRole = (roleId, personId) => {
  const skillGap = getSkillGap(roleId, personId);
  if (!skillGap.length) return { matchPct: 0, skillsToDevelop: 0 };
  const total = skillGap.reduce((sum, s) => sum + s.required, 0);
  const achieved = skillGap.reduce((sum, s) => sum + Math.min(s.current, s.required), 0);
  return {
    matchPct: Math.round((achieved / total) * 100),
    skillsToDevelop: skillGap.filter((s) => s.current < s.required).length,
  };
};

// ─── Career pathways (employee view) ─────────────────────────────────────────
// `steps` is the full ordered progression from the employee's current role to
// their target role (req #5) — first entry is always CURRENT_EMPLOYEE's
// current role. Deliberately mixes vertical promotions with lateral/
// cross-functional moves (analytics → data engineering → platform
// engineering → engineering leadership) rather than a straight single-track
// climb, since that's how a Business Analyst realistically reaches a CTO-level
// target — reuses existing ROLES entries end-to-end so no role's own
// fields (level, filters, etc.) need to change. `opportunities` are the Open
// Positions shown under the step chain, sourced from roles along the path
// (req #5b) — not just the immediate next step.

export const PATHWAYS = [
  {
    id: "pw-analytics-lead",
    label: "Data Leadership Track",
    steps: [
      "r-business-analyst",
      "r-sr-analyst",
      "r-sr-data-eng",
      "r-sr-swe",
      "r-em-data-eng",
      "r-vp-eng",
      "r-cto",
    ],
    opportunities: [
      { id: "op-1", roleId: "r-sr-analyst" },
      { id: "op-2", roleId: "r-sr-swe" },
    ],
  },
  {
    id: "pw-business-analyst",
    label: "Business Analysis Track",
    steps: ["r-business-analyst", "r-sr-analyst"],
    opportunities: [{ id: "op-3", roleId: "r-sr-analyst" }],
  },
];

// Approx. typical time spent in a role before the next move along a pathway
// (years) — display-only, no backend source yet. The final/target role in a
// chain has no entry since it's the destination, not a rung to move on from.
export const ROLE_TENURE_YEARS = {
  "r-business-analyst": 2,
  "r-sr-analyst": 2,
  "r-sr-data-eng": 2,
  "r-sr-swe": 1.5,
  "r-em-data-eng": 3,
  "r-em-platform": 3,
  "r-analytics-mgr": 3,
  "r-vp-eng": 4,
  "r-vp-data": 4,
  "r-swe-ii": 2,
};

export const getPathwaySteps = (pathway) =>
  pathway.steps.map((roleId) => ROLES.find((r) => r.id === roleId)).filter(Boolean);

// Pathway-level tags, derived automatically from each step's role-level
// `function`/`involvesGeneralManagement` fields — never stored, always
// computed fresh so a pathway's tags can't drift from its steps' actual
// role data. Powers the Career Pathways "Within Function" / "Cross
// Function" / "Involves General Management" filter checkboxes (OR logic
// across whichever boxes are checked).
export const getPathwayTags = (pathway, currentRoleId) => {
  const steps = getPathwaySteps(pathway);
  const currentRole = ROLES.find((r) => r.id === currentRoleId);
  const currentFunction = currentRole?.function;
  const withinFunction = steps.every((r) => r.function === currentFunction);
  const crossFunction = steps.some((r) => r.function !== currentFunction);
  const involvesGeneralManagement = steps.some((r) => r.involvesGeneralManagement);
  return { withinFunction, crossFunction, involvesGeneralManagement };
};

export const getOpportunity = (opportunityId) => {
  for (const pathway of PATHWAYS) {
    const found = pathway.opportunities.find((o) => o.id === opportunityId);
    if (found) return { ...found, pathway };
  }
  return null;
};
